import type {ReactiveController, ReactiveControllerHost} from 'lit';
import {appState} from '../../app.state';

/**
 * Reactive Controller that derives `appState.currentMode` from the current URL.
 *
 * This controller is the SOLE writer of `appState.currentMode` (TD-002 F6).
 * `appState.currentMode` itself is a plain `@property()` (no `@storage()`),
 * so the URL stays authoritative across reloads. `localStorage.lastMode` is
 * a separate persistence sink consulted only by the cold-start route guard
 * to decide where `/` should redirect on first visit.
 *
 * Rules:
 * - Any pathname starting with `/knowledge` → `'wissen'`
 * - Any pathname starting with `/resource-management` → `'resource'` (O2D-PRD-100, TR2)
 * - Any pathname starting with `/linkview` → `'linkview'` (Stefan, 2026-09-03)
 * - Any pathname starting with `/personal-organizer` → `'personal-organizer'` ("My Organizer")
 * - Any pathname starting with `/gimmicks` → `'gimmicks'` (Anke, 2026-11-01)
 * - Anything else → `'todo'`
 *
 * Lifecycle:
 * - `hostConnected()` runs `syncFromUrl()` once and attaches the `popstate` listener
 * - `hostDisconnected()` removes the listener using the stored reference
 */
export class ModeRouterController implements ReactiveController {

    private readonly _onPopState: () => void = () => this.syncFromUrl();

    constructor(private readonly _host: ReactiveControllerHost) {
        _host.addController(this);
    }

    /** Derive mode from `window.location.pathname` and write it to AppState + localStorage. */
    public syncFromUrl(): void {
        const pathname: string = window.location.pathname;
        // The bare root '/' is a transient cold-start redirect entry, NOT a mode. Deriving
        // 'todo' from it and persisting lastMode here would clobber the saved lastMode
        // BEFORE the home-enter guard (routes.ts) reads it → cold-start always lands on
        // /by-board regardless of the last used mode (#73, affected all modes). Leave both
        // currentMode and lastMode untouched; the guard redirects to the real mode path and
        // dispatches a popstate, so this runs again against that final URL.
        if (pathname === '/') {
            return;
        }
        const mode: 'todo' | 'wissen' | 'resource' | 'linkview' | 'personal-organizer' | 'gimmicks' = pathname.startsWith('/knowledge')
            ? 'wissen'
            : pathname.startsWith('/resource-management')
                ? 'resource'
                : pathname.startsWith('/linkview')
                    ? 'linkview'
                    : pathname.startsWith('/personal-organizer')
                        ? 'personal-organizer'
                        : pathname.startsWith('/gimmicks')
                            ? 'gimmicks'
                            : 'todo';
        if (appState.currentMode !== mode) {
            appState.currentMode = mode;
        }
        localStorage.setItem('lastMode', mode);
    }

    public hostConnected(): void {
        this.syncFromUrl();
        window.addEventListener('popstate', this._onPopState);
    }

    public hostDisconnected(): void {
        window.removeEventListener('popstate', this._onPopState);
    }
}
